import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLenis, scrollToSection } from "../../hooks/useLenis";

/**
 * Same-page / home-page hash navigation that works with Lenis.
 *
 * Native smooth scroll + Lenis used to fight each other (especially across
 * the tall sticky portfolio), so section jumps stopped mid-way and needed
 * several clicks. We own the scroll entirely through Lenis.
 */
export default function HashScroll() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  /* ── Programmatic scroll when route hash changes ── */
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~40 frames while HomePage / Lenis mount

    const run = () => {
      if (cancelled) return;

      if (!hash || hash === "#") {
        // No section target — jump to top (home, blog, case study, …)
        scrollToSection("top", { immediate: true, lock: false });
        return;
      }

      const id = hash.replace(/^#/, "");
      if (id === "top") {
        scrollToSection("top");
        return;
      }

      const el = document.getElementById(id);
      // On home, wait briefly for Lenis + section mount (e.g. /blog → /#o-nas)
      if (pathname === "/" && (!el || !getLenis()) && attempts < maxAttempts) {
        attempts += 1;
        requestAnimationFrame(run);
        return;
      }

      if (!el) return;
      scrollToSection(id);
    };

    // Defer one frame so layout (portfolio sticky height etc.) is ready
    const raf = requestAnimationFrame(run);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [pathname, hash]);

  /* ── Intercept in-page hash link clicks so the browser never
        starts its own scroll (which cancels Lenis mid-flight) ── */
  useEffect(() => {
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest?.("a[href]");
      if (!anchor) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (!url.hash) return;

      const targetPath = url.pathname || "/";
      const currentPath = window.location.pathname || "/";

      // Cross-page navigation: let the browser / React Router handle it.
      // HashScroll effect will scroll once the home page mounts.
      if (targetPath !== currentPath) return;

      // Same path + hash link — own the scroll completely
      event.preventDefault();

      const nextHash = url.hash;
      const id = nextHash.slice(1) || "top";

      // Same hash again (e.g. re-click "O nas") — effect won't re-run, scroll now
      if (window.location.hash === nextHash) {
        scrollToSection(id);
        return;
      }

      // Different hash — update URL; the effect above performs the scroll
      navigate(
        { pathname: targetPath, hash: nextHash },
        { preventScrollReset: true }
      );
    };

    // Capture phase: run before React handlers / Magnetic wrappers
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [navigate]);

  return null;
}
